import { useQuery } from '@apollo/client'
import { TACHO_DRIVER_FILES_QUERY } from '../queries'
import { Card, Input, Select } from '@/components_deprecated'
import styled from 'styled-components'
import {
  Button,
  ModuleHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useNavigate } from 'react-router'
import type { ReactNode } from 'react'
import { useCallback, useState } from 'react'
import { format, useDebounce } from '@/utils'
import { parseISO } from 'date-fns'
import { Icon, Loader } from '@ur-solutions/react-components'
import { isMobile } from 'react-device-detect'
import { getNodes } from '@/utils/graphql'
import { UploadTachoDriverFile } from '../components/overview/UploadTachoDriverFile'
import { DownloadFileButton } from '../components/overview/download-file-button'
import { DownloadTachoFilesButton } from '../components/download-files'
import { formatTachoFileSource } from '../consts'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
  position: relative;
`

const Controls = styled(Card)`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 1rem;
`

const PAGE_SIZE = 20

interface Props {
  tabs?: ReactNode
}

export const ActivitiesFileArchive: React.FC<Props> = ({ tabs }) => {
  const translations = useTranslate({
    fileArchive: 'activities.file-archive',
    user: 'common.user',
    source: 'common.source',
    fileName: 'common.filename',
    processed: 'common.processed',
    uploadedAt: 'common.date',
    week0: ['common.week-n', { n: '0' }],
    weekN: ['common.week-n', { n: '0' }],
    search: 'common.search',
    loadMore: 'common.load-more',
    allFiles: 'common.all-files',
    processedFiles: 'common.processed-files',
    filesInQueue: 'common.files-in-queue',
  })

  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [processed, setProcessed] = useState('ALL')
  const debouncedSearch = useDebounce(search, 300)

  const { data, loading, fetchMore, refetch } = useQuery(
    TACHO_DRIVER_FILES_QUERY,
    {
      variables: {
        first: PAGE_SIZE,
        after: null,
        q: debouncedSearch,
        processed: processed === 'ALL' ? null : processed === 'PROCESSED',
      },
    }
  )

  const files = getNodes(data?.allTachoDriverFiles)

  const handleFetchMore = useCallback(async () => {
    if (typeof data === 'undefined') return
    if (loading || data?.allTachoDriverFiles?.pageInfo?.endCursor === null)
      return
    try {
      await fetchMore({
        variables: {
          first: PAGE_SIZE,
          after: data?.allTachoDriverFiles?.pageInfo?.endCursor,
          q: debouncedSearch,
        },
      })
    } catch (e) {
      if (e instanceof Error && e.name === 'Invariant Violation') return
      throw e
    }
  }, [data, debouncedSearch, fetchMore, loading])

  const fileSelectOptions = [
    { value: 'ALL', label: translations.allFiles },
    { value: 'PROCESSED', label: translations.processedFiles },
    { value: 'IN_QUEUE', label: translations.filesInQueue },
  ]

  return (
    <Wrapper>
      <ModuleHeader
        className="whitespace-nowrap"
        contentClassName="items-center"
        title={translations.fileArchive}
      >
        <DownloadTachoFilesButton mode="driver" />
        <UploadTachoDriverFile onUploadSuccess={() => void refetch()} />
      </ModuleHeader>
      {tabs}
      <Controls>
        <Input
          placeholder={translations.search}
          value={search}
          onChange={val => setSearch(val)}
          grayed
          fullWidth={isMobile}
        />

        <Select
          value={processed}
          options={fileSelectOptions}
          onChange={val => setProcessed(val ?? 'ALL')}
        />
      </Controls>
      {loading && <Loader.Dots />}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{translations.user}</TableHead>
              <TableHead>{translations.source}</TableHead>
              <TableHead>{translations.fileName}</TableHead>
              <TableHead>{translations.processed}</TableHead>
              <TableHead>{translations.uploadedAt}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {files?.map(file => (
              <TableRow
                key={file.id}
                className="cursor-pointer"
                onClick={() => navigate(`/analytics/file-archive/${file.id}`)}
              >
                <TableCell>
                  {file.user?.fullName || file.driverCardNumber}
                </TableCell>
                <TableCell>{formatTachoFileSource(file.source)}</TableCell>
                <TableCell>{file.fileName}</TableCell>
                <TableCell>
                  {file.processed && file.postProcessed ? (
                    <Icon icon="check" color="green" />
                  ) : file.processed || file.postProcessed ? (
                    <Icon icon="clock" color="orange" />
                  ) : (
                    <Icon icon="times" color="red" />
                  )}
                </TableCell>
                <TableCell>
                  {format(parseISO(file.createdAt), 'dd. MMM yyyy HH:mm')}
                </TableCell>
                <TableCell
                  className="text-right"
                  onClick={e => e.stopPropagation()}
                >
                  <DownloadFileButton
                    url={file.file}
                    fileName={file.fileName}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data?.allTachoDriverFiles?.pageInfo?.hasNextPage && !loading && (
          <Button onClick={handleFetchMore}>{translations.loadMore}</Button>
        )}
      </Card>
    </Wrapper>
  )
}
